/**
 * Список документів «Фінансова звітність» (ч.1386) — за зразком декларацій єдинника:
 * InfiniteDocGrid + статус подання з Журналу Кабінету ДПС.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  FORM_SET_TITLES, PERIOD_LABELS, financialStatements, fmtUah, type FinancialStatement,
} from '@/api/financialStatements';
import { dpsSubmissions } from '@/api/dpsSubmissions';
import { orgs as orgsApi } from '@/api/catalogs';
import { DocListFiltersRow } from '@/shared/components/DocListFilters';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const SUB_STATUS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', 'var(--muted)'],
  SIGNED: ['підписана', 'var(--badge-blue-fg)'],
  SENT: ['відправлена', 'var(--badge-blue-fg)'],
  DELIVERED: ['обробляється', 'var(--badge-blue-fg)'],
  ACCEPTED: ['прийнята', 'var(--badge-green-fg)'],
  REJECTED: ['не прийнята', 'var(--badge-red-fg)'],
  ERROR: ['помилка', 'var(--badge-red-fg)'],
};

export default function FinancialStatementsPage() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('reports.regulated.manage');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgsApi.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgListQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgListQ.data]);
  const [orgId, setOrgId] = useOrgFilter();
  const period = useDefaultPeriod();
  const [fYear, setFYear] = useState<string>(() => period.dateFrom ? period.dateFrom.slice(0, 4) : '');
  const [fQuarter, setFQuarter] = useState<string>('');
  const { datasource, total } = useInfiniteGridDatasource<FinancialStatement>(
    (limit, offset, sort) => financialStatements.list({
      organization_id: orgId || undefined,
      period_year: fYear ? Number(fYear) : undefined,
      period_quarter: fQuarter ? Number(fQuarter) : undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, fYear, fQuarter],
  );
  const subsQ = useQuery({
    queryKey: ['financial-statement-subs'],
    queryFn: () => dpsSubmissions.list({ source_type: 'doc.financial_statements', limit: 500 }),
  });
  const subByDoc = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subsQ.data?.items ?? []) {
      if (s.source_id && !m.has(s.source_id)) m.set(s.source_id, s.status);
    }
    return m;
  }, [subsQ.data]);

  const cols = useMemo<ColDef<FinancialStatement>[]>(() => [
    { colId: 'period', headerName: 'Період', width: 150,
      valueGetter: (p) => p.data ? `${PERIOD_LABELS[p.data.period_quarter]} ${p.data.period_year}` : '' },
    { field: 'form_set', headerName: 'Форми', width: 250,
      valueFormatter: (p) => p.value ? FORM_SET_TITLES[p.value as keyof typeof FORM_SET_TITLES] : '' },
    { field: 'document_number', headerName: '№', width: 170 },
    { field: 'organization_id', headerName: 'Організація', width: 220,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '' },
    { colId: 'balance', headerName: 'Баланс, грн', width: 150, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueGetter: (p) => p.data ? (p.data.values?.R1300G4 ?? '0') : null,
      valueFormatter: (p) => p.data ? fmtUah(p.value) : '' },
    { colId: 'submission', headerName: 'Подання', width: 140, sortable: false,
      cellRenderer: (p: { data?: FinancialStatement }) => {
        if (!p.data) return '';
        const st = subByDoc.get(p.data.id);
        if (!st) return <span style={{ color: 'var(--muted)' }}>—</span>;
        const [txt, color] = SUB_STATUS[st] ?? [st, 'inherit'];
        return <span style={{ color, fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      } },
    { field: 'created_at', headerName: 'Створено', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '' },
  ], [subByDoc, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Фінансова звітність<TitleTotal total={total} /></h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/reports/financial/view')}
            title="Подивитись форми за період без створення документа (у гривнях з копійками)">
            перегляд за період
          </button>
          <button className="primary"
            onClick={() => navigate('/reports/financial/new')}
            disabled={!hasPermissionInAnyOrg}
            title={!hasPermissionInAnyOrg ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій` : undefined}>
            + Створити за період
          </button>
        </div>
      </div>
      <DocListFiltersRow>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={fQuarter} onChange={(e) => setFQuarter(e.target.value)} style={{ width: 150, flex: '0 0 auto' }}>
          <option value="">— період —</option>
          {[1, 2, 3, 4].map((q) => <option key={q} value={String(q)}>{PERIOD_LABELS[q]}</option>)}
        </select>
        <select value={fYear} onChange={(e) => setFYear(e.target.value)} style={{ width: 110, flex: '0 0 auto' }}>
          <option value="">— рік —</option>
          {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() + 1 - i).map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<FinancialStatement>
        datasource={datasource}
        columnDefs={cols}
        storageKey="financial-statements"
        onRowDoubleClicked={(r) => navigate(`/reports/financial/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
